import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  TableSortLabel,
  Typography
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import dayjs from 'dayjs';

const EventList = ({ events, onEdit, onDelete, sortBy, sortOrder, onSort }) => {
  const [hoveredRow, setHoveredRow] = useState(null);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return dayjs(date).format('MMM DD, YYYY');
  };

  const handleSort = (field) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    onSort(field, newOrder);
  };

  const sortedEvents = [...events].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'title':
        aValue = a.title?.toLowerCase() || '';
        bValue = b.title?.toLowerCase() || '';
        break;
      case 'category':
        aValue = a.category?.toLowerCase() || '';
        bValue = b.category?.toLowerCase() || '';
        break;
      case 'location':
        aValue = a.location?.toLowerCase() || '';
        bValue = b.location?.toLowerCase() || '';
        break;
      case 'eventDate':
      default:
        aValue = a.eventDate ? new Date(a.eventDate).getTime() : 0;
        bValue = b.eventDate ? new Date(b.eventDate).getTime() : 0;
        break;
    }

    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  if (events.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          No events found. Add your first event to get started!
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortBy === 'eventDate'}
                direction={sortBy === 'eventDate' ? sortOrder : 'asc'}
                onClick={() => handleSort('eventDate')}
              >
                Date
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === 'title'}
                direction={sortBy === 'title' ? sortOrder : 'asc'}
                onClick={() => handleSort('title')}
              >
                Title
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === 'category'}
                direction={sortBy === 'category' ? sortOrder : 'asc'}
                onClick={() => handleSort('category')}
              >
                Category
              </TableSortLabel>
            </TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Tags</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEvents.map((event) => (
            <TableRow
              key={event.id}
              hover
              onMouseEnter={() => setHoveredRow(event.id)}
              onMouseLeave={() => setHoveredRow(null)}
            >
              <TableCell>{formatDate(event.eventDate)}</TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {event.title}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={event.category || 'N/A'}
                  size="small"
                  color={event.category === 'work' ? 'primary' : 'secondary'}
                />
              </TableCell>
              <TableCell>{event.location || 'N/A'}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {event.tags && event.tags.length > 0 ? (
                    event.tags.slice(0, 3).map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No tags
                    </Typography>
                  )}
                  {event.tags && event.tags.length > 3 && (
                    <Typography variant="caption" color="text.secondary">
                      +{event.tags.length - 3} more
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {event.description || 'No description'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  onClick={() => onEdit(event)}
                  color="primary"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onDelete(event.id)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EventList;





